import {Box, Button, Grid, TextField} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import {useDispatch, useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import {useEffect, useState} from 'react';
import Command from '../../state/actions/command';
import {DateTime} from 'luxon';
import {DesktopDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
import SummaryCard from './dashboard/SummaryCard';
import RevenueByProjectTable from './dashboard/RevenueByProjectTable';
import RevenueByEmployeeTable from './dashboard/RevenueByEmployeeTable';
import EmployeeRateDialog from './dashboard/EmployeeRateDialog';
import ProjectRateDialog from './dashboard/ProjectRateDialog';
import YearlyRevenueChart from './dashboard/YearlyRevenueChart';
import {DashboardProject} from '../../state/types/Dashboard';

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

const DashboardPage = () => {
    const dispatch = useDispatch();
    const dashboardData = useSelector(Selector.Dashboard.data);
    const yearlyRevenue = useSelector(Selector.Dashboard.yearlyRevenue);
    const [selectedMonth, setSelectedMonth] = useState<DateTime | null>(DateTime.now().startOf('month'));
    const [rateDialogOpen, setRateDialogOpen] = useState(false);
    const [projectRateDialogOpen, setProjectRateDialogOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<DashboardProject | null>(null);

    useEffect(() => {
        if (selectedMonth) {
            dispatch(Command.Dashboard.fetchDashboardData({
                month: selectedMonth.toFormat('yyyy-MM'),
            }));
            dispatch(Command.Dashboard.fetchYearlyRevenue({
                year: selectedMonth.year,
            }));
        }
    }, [selectedMonth]);

    useEffect(() => {
        dispatch(Command.Dashboard.fetchEmployeeRateList({}));
    }, []);

    const openProjectRateDialog = (project: DashboardProject) => {
        setSelectedProject(project);
        setProjectRateDialogOpen(true);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3, gap: 2 }}>
                <DesktopDatePicker
                    label={'Monat'}
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    renderInput={(params) => <TextField {...params} size={'small'} />}
                    views={['month', 'year']}
                    inputFormat={'MMMM yyyy'}
                    maxDate={DateTime.now()}
                />
                <Button
                    variant={'outlined'}
                    startIcon={<SettingsIcon />}
                    onClick={() => setRateDialogOpen(true)}
                >
                    Stundensätze
                </Button>
            </Box>

            <Grid container={true} spacing={3} sx={{ marginBottom: 3 }}>
                <Grid item={true} xs={12} md={4}>
                    <SummaryCard
                        title={'Gesamtstunden'}
                        value={dashboardData ? `${dashboardData.totalHours.toFixed(1)} h` : '0 h'}
                        subtitle={selectedMonth ? selectedMonth.toFormat('MMMM yyyy') : ''}
                        icon={<AccessTimeIcon sx={{ fontSize: 40 }} />}
                    />
                </Grid>
                <Grid item={true} xs={12} md={4}>
                    <SummaryCard
                        title={'Gesamtumsatz'}
                        value={dashboardData ? formatCurrency(dashboardData.totalRevenue) : '0,00 €'}
                        subtitle={'Basierend auf Stundensätzen'}
                        icon={<EuroIcon sx={{ fontSize: 40 }} />}
                        color={'#ff9900'}
                    />
                </Grid>
                <Grid item={true} xs={12} md={4}>
                    <SummaryCard
                        title={'Aktive Projekte'}
                        value={dashboardData ? String(dashboardData.projects.length) : '0'}
                        subtitle={'Mit Zeiterfassung in diesem Monat'}
                        icon={<FolderIcon sx={{ fontSize: 40 }} />}
                        color={'#4caf50'}
                    />
                </Grid>
            </Grid>

            <Box sx={{ marginBottom: 3 }}>
                <RevenueByProjectTable
                    projects={dashboardData?.projects || []}
                    onEditRate={openProjectRateDialog}
                />
            </Box>

            <Grid container={true} spacing={3} sx={{ marginBottom: 3 }}>
                <Grid item={true} xs={12}>
                    <RevenueByEmployeeTable
                        employees={dashboardData?.employees || []}
                        totalHours={dashboardData?.totalHours || 0}
                    />
                </Grid>
            </Grid>

            <Box sx={{ marginBottom: 3 }}>
                <YearlyRevenueChart
                    data={yearlyRevenue}
                    onChangeYear={(year) => dispatch(Command.Dashboard.fetchYearlyRevenue({ year }))}
                />
            </Box>

            <EmployeeRateDialog
                open={rateDialogOpen}
                onClose={() => {
                    setRateDialogOpen(false);
                    if (selectedMonth) {
                        dispatch(Command.Dashboard.fetchDashboardData({
                            month: selectedMonth.toFormat('yyyy-MM'),
                        }));
                    }
                }}
            />
            <ProjectRateDialog
                open={projectRateDialogOpen}
                onClose={() => {
                    setProjectRateDialogOpen(false);
                    if (selectedMonth) {
                        dispatch(Command.Dashboard.fetchDashboardData({
                            month: selectedMonth.toFormat('yyyy-MM'),
                        }));
                    }
                }}
                project={selectedProject}
            />
        </Box>
    );
};

export default DashboardPage;
